<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ $t('addItemToStockModalTitle') }}</div>
    </template>
    <Spinner v-if="dataIsSending"/>
    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="reduced_position_code_modal" disabled v-model="itemData.code" autocomplete="new-password"/>
          <label for="reduced_position_code_modal">{{ $t('Code') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="reduced_position_name_modal" disabled v-model="itemData.name" autocomplete="new-password"/>
          <label for="reduced_position_name_modal">{{ $t('Name') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputNumber id="reduced-position-qty-modal"
                       :locale="computedNumberInputLocale"
                       :inputClass="{'p-invalid' : submitted && !itemData.qty}"
                       v-model="itemData.qty"
                       mode="decimal"
                       disabled
                       :min="0"
                       :minFractionDigits="2"
                       :maxFractionDigits="4"
                       autocomplete="off"/>
          <label for="reduced-position-qty-modal">{{ $t('Qty') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.qty">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputNumber id="reduced-position-purchase-price-modal"
                       :locale="computedNumberInputLocale"
                       :inputClass="{'p-invalid' : submitted && !itemData.purchase_price}"
                       v-model="itemData.purchase_price"
                       mode="decimal"
                       disabled
                       :min="0"
                       :minFractionDigits="2"
                       :maxFractionDigits="4"
                       autocomplete="off"/>
          <label for="reduced-position-purchase-price-modal">{{ $t('Purchase price') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.purchase_price">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <div class="p-float-label dropdown-wrapper table-body__name-input--first">
          <CustomDropdown id="add-reduced-position-warehouse-item"
                          class="dropdown__select dropdown__select--one-button-width dropdown__select--border-radius"
                          :class="{'p-invalid' : submitted && !selectedWarehouseItem}"
                          v-model="selectedWarehouseItem"
                          @filter="searchWarehouseItems($event)"
                          :filterPlaceholder="$t('lineItemSearchStockItemPlaceholder')"
                          :placeholder="$t('lineItemSelectStockItemPlaceholder')"
                          data-key="id"
                          :clearSearchData="!visible"
                          :options="warehouseItemsData"
                          :filter="true"
                          :filterFields="['code', 'raw_code', 'name']"
                          :showClear="true">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>
                  {{ slotProps.value.name }}
                  <span v-if="slotProps.value.code">, {{ $t('Code') }}: {{ slotProps.value.code }}</span>
                </span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <div>{{ slotProps.option.name }}<span v-if="slotProps.option.code">, {{$t('Code')}}: {{ slotProps.option.code }}</span></div>
                <span style="font-size: 0.9rem">{{$t('Qty')}}: {{ formatDecimal(slotProps.option.qty) }}, {{ $t('reservedShortLabel') }}: {{ formatDecimal(slotProps.option.reserved_qty) }}, {{ $t('lineItemAvailableQtyLabel') }}: {{ formatDecimal(Number(slotProps.option.qty || 0) - Number(slotProps.option.reserved_qty || 0)) }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="add-reduced-position-warehouse-item">{{ $t('Stock item') }}<span class="warning-color">*</span></label>
          <div class="p-d-flex p-flex-column">
            <Button :disabled="!selectedWarehouseItem" style="height: 16.5px; border-bottom-right-radius: 0" @click="editWarehouseItem" class="dropdown__create-button" :icon="'ti-pencil'"/>
            <Button style="height: 16.5px; border-top-right-radius: 0" @click="addNewWarehouseItem" class="dropdown__create-button" :icon="'ti-plus'"/>
          </div>

        </div>
        <small class="p-invalid" v-if="submitted && !selectedWarehouseItem">{{ $t('Required field') }}</small>
<!--        <small class="p-invalid" v-if="itemData.purchase_price && selectedWarehouseItem && itemData.purchase_price > selectedWarehouseItem.purchase_price">-->
        <small class="p-invalid" v-if="purchasePriceIsHigh()">
          <div class="p-d-flex p-ai-center p-mt-1">
            <i class="ti-info-alt p-mr-1"></i>
            <div>{{ $t('purchasePriceIsHighAlert') }}</div>
          </div>
        </small>
        <div class="p-text-secondary p-mt-1">
          <small> {{ $t('addItemToStockHelp') }}</small>
        </div>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <Button :label="$t('Save')"
              :disabled="disableSaveButton"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>

    <WarehouseItemModal :visible="warehouseItemModal"
                        :item="warehouseItem"
                        :modalLayer="2"
                        @close="closeWarehouseItemModal"
                        @update-item="updateWarehouseItem"/>

    <ConfirmModal :visible="confirmPurchasePriceIsTooHighModal"
                  :text="$t('purchasePriceIsHighAlert') + ' ' + $t('confirm.proceed')"
                  @close="closeConfirmPurchasePriceIsTooHighModal"
                  @confirm="saveItemAnyway"/>
  </Modal>
</template>

<script>
import httpClient from "@/services/http.services";
import generateMixins from "@/mixins/generateMixins";
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import WarehouseItemModal from "@/pages/warehouse/components/WarehouseItemModal";
import showErrorsMixins from "@/mixins/showErrorsMixins";

export default {
  mixins: [ generateMixins, formatMixins, showErrorsMixins ],
  emits: ['close', 'update-items', 'update-item'],
  components: { WarehouseItemModal },
  name: 'AddReducedPositionToWarehouseModal',
  props: {
    item: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      confirmPurchasePriceIsTooHighModal: false,
      ignorePurchasePriceIsTooHigh: false,
      dataIsSending: false,
      disableSaveButton: false,
      warehouseItem: null,
      warehouseItemModal: false,
      warehouseItemsData: [],
      selectedWarehouseItem: null,
      itemData: {},
      submitted: false,
    }
  },
  watch: {
    item(value) {
      if (!value) {
        this.itemData = {}
        this.selectedWarehouseItem = null
        this.warehouseItemsData = []
        return
      }

      this.itemData = {
        ...value,
        code: value.position_data?.code,
        raw_code: value.position_data?.raw_code,
        name: value.position_data?.name,
        purchase_price: Number(value.position_data?.purchase_price || 0),
        sell_price: Number(value.position_data?.sell_price || 0),
        qty: Number(value.qty || 0),
      }

      if (value.warehouse_item) {
        this.selectedWarehouseItem = value.warehouse_item

        const warehouseItemExists = this.warehouseItemsData.find(i => Number(i.id) === Number(value.warehouse_item.id))

        if (!warehouseItemExists) {
          this.warehouseItemsData.push(value.warehouse_item)
        }
      } else {
        this.selectedWarehouseItem = null
        this.warehouseItemsData = []
      }
    },
    '$store.state.secondLayerIsOpened'() {
      if (!this.$store.state.secondLayerIsOpened) {
        if (this.warehouseItemModal) {
          this.closeWarehouseItemModal()
        }
        if (this.confirmPurchasePriceIsTooHighModal) {
          this.closeConfirmPurchasePriceIsTooHighModal()
        }
      }
    },
    visible() {
      if (!this.visible) {
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.selectedWarehouseItem = null
        this.ignorePurchasePriceIsTooHigh = false
      }
    },
  },
  methods: {
    openConfirmPurchasePriceIsTooHighModal() {
      this.confirmPurchasePriceIsTooHighModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    closeConfirmPurchasePriceIsTooHighModal() {
      this.confirmPurchasePriceIsTooHighModal = false
      this.$store.commit('toggleSecondLayer', false)

    },
    // updateWarehouseItem(warehouseItem) {
    //   if (!warehouseItem) return false
    //   this.selectedWarehouseItem = warehouseItem
    //   if (warehouseItem.isNew) {
    //     this.warehouseItemsData.push(warehouseItem)
    //   }
    //   // this.searchData = null
    // },
    updateWarehouseItem(warehouseItem) {
      if (!warehouseItem) return false

      this.selectedWarehouseItem = warehouseItem

      const index = this.warehouseItemsData.findIndex(item => Number(item.id) === Number(warehouseItem.id))

      if (index >= 0) {
        this.warehouseItemsData[index] = warehouseItem
      } else {
        this.warehouseItemsData.push(warehouseItem)
      }
    },
    closeWarehouseItemModal() {
      this.warehouseItemModal = false
      this.warehouseItem = {}
      this.$store.commit('toggleSecondLayer', false)
    },
    async searchWarehouseItems(event) {
      const search = String(event?.value ?? '').trim()

      if (!search) {
        this.warehouseItemsData = []
        return
      }

      try {
        const { status, data } = await httpClient('warehouse/get-dropdown-items?search=' + encodeURIComponent(search))

        if (status === 200 && data?.success) {
          this.warehouseItemsData = Array.isArray(data.items) ? data.items : []
        } else {
          this.warehouseItemsData = []
          this.showUnknownError()
        }
      } catch (err) {
        this.warehouseItemsData = []
        this.showError(err)
      }
    },
    addNewWarehouseItem() {
      this.warehouseItem = {
        code: this.itemData.code,
        raw_code: this.itemData.raw_code ? this.itemData.raw_code : (this.itemData.code ? this.generateRawCode(this.itemData.code) : null),
        name: this.itemData.name,
        purchase_price: this.itemData.purchase_price,
        sell_price: this.itemData.sell_price,
      }

      this.warehouseItemModal = true
      this.$store.commit('toggleSecondLayer', true)
    },

    editWarehouseItem() {
      if (!this.selectedWarehouseItem) return
      this.warehouseItem = {...this.selectedWarehouseItem}

      this.warehouseItemModal = true
      this.$store.commit('toggleSecondLayer', true)
    },
    saveItemAnyway() {
      this.ignorePurchasePriceIsTooHigh = true
      this.closeConfirmPurchasePriceIsTooHighModal()
      this.saveItem()
    },
    purchasePriceIsHigh() {
      if (!this.selectedWarehouseItem) return false

      return Number(this.itemData?.purchase_price || 0) > Number(this.selectedWarehouseItem?.purchase_price || 0)
    },
    async saveItem() {
      if (!this.itemData) return
      this.submitted = true
      if (!this.selectedWarehouseItem || !this.itemData.qty) {
        return
      }

      if (this.purchasePriceIsHigh() && !this.ignorePurchasePriceIsTooHigh) {
        this.openConfirmPurchasePriceIsTooHighModal()
        return false
      }

      this.dataIsSending = true
      this.disableSaveButton = true

      const item = {
        reducedPositionId: this.itemData?.id ?? null,
        warehouseItemId: this.selectedWarehouseItem?.id ?? null,
        qty: this.itemData?.qty ?? null,
      }

      try {
        const { data, status } = await httpClient.post(`reduced-position/add-to-warehouse`, item)
        if (status === 200 && data?.success) {
          this.$emit('update-items', this.itemData.id)
          this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife});
          this.close()
          this.$store.commit('updateWarehouseRequestsCount', data.warehouse_requests_count ?? 0)
        } else if (data?.error) {
          this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife});
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.disableSaveButton = false
        this.dataIsSending = false
      }
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '95%', maxWidth: '400px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, maxWidth: '400px', marginTop: '25px'}
      } else {
        return {position: 'absolute', top: 0, maxWidth: '400px', marginTop: '50px'}
      }
    },
  },

}
</script>

<style scoped lang="scss">
.dropdown-wrapper {
  display: flex;
}

.dropdown__select {
  width: 100%;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.dropdown__select--one-button-width {
  width: calc(100% - 2.357rem);
}

.dropdown__create-button{
  width: 2.357rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>